<script setup lang="ts">
// Waterfall (stacked-bar) chart. Each row in `data` is one step;
// `value` is the delta. Categories named in `spec.totals[]` render as
// absolute bars from zero (Opening MRR / Closing MRR / sub-totals).
// Everything else is a delta — host computes a transparent placeholder
// stack underneath each delta bar so the visible bar starts at the
// running total.
//
// Connector lines between adjacent bars sit at the running cumulative
// at each boundary (top of bar i = bottom-of-positive / top-of-negative
// of bar i+1), so the step flow reads as continuous instead of as a
// row of disconnected floating rectangles. Drawn at the level *after*
// bar i is applied (= the level *before* bar i+1 starts); if a declared
// total disagrees with the running sum, the connector reveals the jog.
//
// Divergent palette defaults to `var(--lucent-chart-positive)` /
// `var(--lucent-chart-negative)` per round-3 GM ring-fence; per-spec
// overrides accept only `var(--*)` references (validator-enforced).
// `divergent.zeroLine` overlays a stronger solid rule at y=0 — useful
// when deltas push the running total below zero (e.g., P&L waterfalls).

import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { WaterfallChartSpec } from "../sbi/protocol";
import { CHART_COLORS, COLORS } from "../theme.ts";
import { baseOption, resolveCssVar, tooltipDefaults } from "./echarts-base";
import { categoryLabelPolicy, plotWidthEstimate } from "./chart-layout";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

const props = withDefaults(
  defineProps<{
    spec: WaterfallChartSpec;
    formats?: Record<string, string>;
  }>(),
  { formats: () => ({}) },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

interface Bar {
  category: string;
  value: number;
  isTotal: boolean;
  /** Lower edge of the visible bar (= cumulative-before for deltas, 0 for totals). */
  base: number;
  /** Running cumulative *after* this bar — i.e. the y-value where the next
   *  bar starts. Connector lines hang off this. For totals this is the
   *  declared total (which resets `running`). */
  cumulativeAfter: number;
}

const bars = computed<Bar[]>(() => {
  const rows = props.spec.data as ReadonlyArray<Record<string, unknown>>;
  const totals = new Set(props.spec.totals ?? []);
  const out: Bar[] = [];
  let running = 0;
  for (const row of rows) {
    const category = String(row[props.spec.category] ?? "");
    const rawValue = row[props.spec.value];
    const value = typeof rawValue === "number" ? rawValue : Number(rawValue);
    if (!Number.isFinite(value)) continue;
    const isTotal = totals.has(category);
    if (isTotal) {
      running = value;
      out.push({ category, value, isTotal, base: 0, cumulativeAfter: running });
    } else {
      const base = value < 0 ? running + value : running;
      running += value;
      out.push({ category, value, isTotal, base, cumulativeAfter: running });
    }
  }
  return out;
});

// Colours: spec override (a `var(--*)` reference per validator) →
// embedder CSS token → canvas-safe theme mirror in `CHART_COLORS`.
// Resolving via `resolveCssVar` preserves the embedder restyle path;
// the hex fallback keeps the bars painting when no token is declared.
const positiveColor = computed(
  () =>
    resolveCssVar(props.spec.divergent?.positiveColor) ??
    resolveCssVar("var(--lucent-chart-positive)") ??
    CHART_COLORS.waterfallPositive,
);
const negativeColor = computed(
  () =>
    resolveCssVar(props.spec.divergent?.negativeColor) ??
    resolveCssVar("var(--lucent-chart-negative)") ??
    CHART_COLORS.waterfallNegative,
);
const totalColor = computed(
  () =>
    resolveCssVar("var(--lucent-waterfall-total)") ??
    CHART_COLORS.waterfallTotal,
);

const option = computed(() => {
  const valueFormat =
    props.spec.format ?? props.formats[props.spec.value];
  const base = baseOption({ title: props.spec.title, yFormat: valueFormat }, ctx.value);
  // Two stacked series: an invisible placeholder + the visible delta/total.
  const placeholderData = bars.value.map((b) => (b.isTotal ? 0 : b.base));
  const visibleData = bars.value.map((b) => Math.abs(b.value));

  // Connector lines between adjacent bars. ECharts `markLine` with a
  // pair of `coord: [categoryName, y]` entries draws a horizontal rule
  // at the running cumulative — sitting at the right edge of bar i and
  // the left edge of bar i+1. Category-axis bars have ~half-band
  // padding on each side, so the line visually bridges the gap between
  // the two bars rather than running through them.
  const connectorData: Array<
    [{ coord: [string, number] }, { coord: [string, number] }]
  > = [];
  for (let i = 0; i < bars.value.length - 1; i++) {
    const a = bars.value[i]!;
    const b = bars.value[i + 1]!;
    connectorData.push([
      { coord: [a.category, a.cumulativeAfter] },
      { coord: [b.category, a.cumulativeAfter] },
    ]);
  }

  // Optional zero-line emphasis. `splitLine` on the y-axis already
  // draws a dashed guide at every tick (including 0 when it falls on
  // one); this overlay paints a stronger solid rule specifically at 0
  // so it reads as the reference line, not just another gridline.
  const zeroLine = props.spec.divergent?.zeroLine
    ? {
        symbol: "none" as const,
        silent: true,
        lineStyle: { color: COLORS.chartLabelStrong, type: "solid" as const, width: 1.5 },
        label: { show: false },
        data: [{ yAxis: 0 }],
      }
    : undefined;

  return {
    ...base,
    xAxis: {
      ...base.xAxis,
      type: "category" as const,
      data: bars.value.map((b) => b.category),
      // Every step matters in a waterfall: rotate (45° → 90°) and
      // truncate before thinning — never silently drop a step label.
      axisLabel: {
        ...base.xAxis.axisLabel,
        ...categoryLabelPolicy({
          labels: bars.value.map((b) => b.category),
          plotWidth: plotWidthEstimate(ctx.value.width),
          mode: "all",
        }),
      },
    },
    yAxis: { ...base.yAxis, type: "value" as const },
    legend: { show: false },
    tooltip: tooltipDefaults("item", ctx.value),
    series: [
      {
        name: "_placeholder",
        type: "bar" as const,
        stack: "wf",
        itemStyle: { color: "transparent" },
        emphasis: { itemStyle: { color: "transparent" } },
        data: placeholderData,
        // Zero-line lives on the placeholder series so it paints behind
        // the visible bars and connectors — overlay order matches z.
        ...(zeroLine ? { markLine: zeroLine } : {}),
      },
      {
        name: "value",
        type: "bar" as const,
        stack: "wf",
        data: bars.value.map((b, i) => ({
          value: visibleData[i],
          itemStyle: {
            color: b.isTotal
              ? totalColor.value
              : b.value >= 0
                ? positiveColor.value
                : negativeColor.value,
          },
        })),
        markLine: {
          symbol: "none" as const,
          silent: true,
          lineStyle: {
            color: COLORS.chartAxisLine,
            type: "solid" as const,
            width: 1,
          },
          label: { show: false },
          data: connectorData,
        },
      },
    ],
  };
});

const height = computed(() =>
  resolveChartHeight("waterfall", props.spec.height),
);

function onClick(p: { seriesName?: string; dataIndex?: number }) {
  if (!props.spec.id || typeof p?.dataIndex !== "number") return;
  if (p.seriesName === "_placeholder") return;
  const bar = bars.value[p.dataIndex];
  if (!bar) return;
  emit("click-target", props.spec.id, {
    category: bar.category,
    value: bar.value,
    isTotal: bar.isTotal,
  });
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    :init-options="{ renderer: 'canvas' }"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
